import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Block as BlockIcon, Print as PrintIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import tesoreriaService, { OrdenPagoDetalleDto } from '../../services/tesoreriaService';
import tesoreriaAplicacionesService, { AplicacionRecibo } from '../../services/tesoreriaAplicacionesService';
import NumeroInternoMeta from '../../components/NumeroInternoMeta';
import VerAsientoContableButton from '../../components/shared/VerAsientoContableButton';
import PDFViewer from '../../components/shared/PDFViewer';
import ObservacionesConNotaProveedor from '../../components/ObservacionesConNotaProveedor';
import api from '../../services/api';
import {
  formatCurrency,
  formatDateLocal,
  formatNroDocumentoVenta,
  formatProveedorConNro,
} from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';
import { tipoToPrefijo } from '../../constants/tiposComprobante';

const formatearFecha = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return '-';
  const s = formatDateLocal(fecha);
  return s || '-';
};

const esOrdenPagoAnulada = (op: OrdenPagoDetalleDto): boolean =>
  (op.asentado ?? '').trim().toUpperCase() === 'A' || (op.nombre ?? '').trim().toUpperCase() === 'ANULADO';

const pathDocumentoAplicado = (nroTipo: string, nroFormulario: number): string | null => {
  if (!nroFormulario || nroFormulario <= 0) return null;
  const tipo = (nroTipo ?? '').trim();
  if (tipo === '1' || tipo === '2') return `/compras/facturas/${nroFormulario}`;
  if (tipo === '3') return `/compras/notas-credito/${nroFormulario}`;
  return null;
};

const OrdenPagoDetalle: React.FC = () => {
  const navigate = useNavigate();
  const { nroFormulario } = useParams<{ nroFormulario: string }>();
  const { enqueueSnackbar } = useSnackbar();

  const [orden, setOrden] = useState<OrdenPagoDetalleDto | null>(null);
  const [aplicaciones, setAplicaciones] = useState<AplicacionRecibo[]>([]);
  const [loading, setLoading] = useState(true);
  const [anulando, setAnulando] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const loadOrden = useCallback(async () => {
    const id = parseInt(nroFormulario || '', 10);
    if (Number.isNaN(id) || id <= 0) {
      enqueueSnackbar('Orden de pago no válida', { variant: 'error' });
      navigate('/tesoreria/orden-pago');
      return;
    }

    try {
      setLoading(true);
      const data = await tesoreriaService.getOrdenPago(id);
      setOrden(data);
      let apps: AplicacionRecibo[] = [];
      try {
        apps = await tesoreriaAplicacionesService.getAplicacionesCreditoProveedor(
          id,
          data.tipoComprobante || '6'
        );
      } catch {
        apps = [];
      }
      if (apps.length === 0 && data.documentosAplicados.length > 0) {
        apps = data.documentosAplicados.map((doc) => ({
          nroIdformPagoCobro: data.nroFormulario,
          nroIdformulario: doc.nroIdformulario,
          nroTipoFormulario: doc.nroTipoFormulario,
          letra: doc.letra,
          nroSuc: doc.nroSuc,
          nroComprobante: doc.nroComprobante,
          fechaEmision: doc.fechaEmision,
          fechaVencim: '',
          importeTotal: doc.importeTotal,
          nroMovCaja: doc.nroMovCaja,
          montoCancelado: doc.montoCancelado,
        }));
      }
      setAplicaciones(apps);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar la orden de pago'), { variant: 'error' });
      navigate('/tesoreria/orden-pago');
    } finally {
      setLoading(false);
    }
  }, [nroFormulario, enqueueSnackbar, navigate]);

  useEffect(() => {
    void loadOrden();
  }, [loadOrden]);

  const handleAnular = async () => {
    if (!orden || anulando || esOrdenPagoAnulada(orden) || !orden.puedeAnular) {
      if (orden?.motivoAnulacion) {
        enqueueSnackbar(orden.motivoAnulacion, { variant: 'warning' });
      }
      return;
    }
    if (
      !window.confirm(
        '¿Está seguro que desea anular esta orden de pago? Se revertirán las imputaciones y los medios de pago asociados.'
      )
    ) {
      return;
    }
    try {
      setAnulando(true);
      await tesoreriaService.anularOrdenPago(orden.nroFormulario);
      enqueueSnackbar('Orden de pago anulada correctamente', { variant: 'success' });
      await loadOrden();
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al anular la orden de pago'), { variant: 'error' });
    } finally {
      setAnulando(false);
    }
  };

  const handleImprimirPDF = async () => {
    if (!orden) return;
    try {
      const response = await api.get(`/api/Tesoreria/ordendepago/${orden.nroFormulario}/pdf`, {
        responseType: 'blob',
      });
      const blob = response.data as Blob;
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al generar el PDF de la orden de pago'), { variant: 'error' });
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#1976d2' }} />
      </Box>
    );
  }

  if (!orden) return null;

  const anulada = esOrdenPagoAnulada(orden);
  const nroDoc = formatNroDocumentoVenta(orden.letra, orden.nroSuc, orden.nroComprobante ?? undefined);
  const totalMedios = orden.mediosDePago.reduce((sum, m) => sum + (m.importe || 0), 0);
  const totalAplicado = aplicaciones.reduce((sum, a) => sum + (a.montoCancelado || 0), 0);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/tesoreria/orden-pago')}
              sx={{ textTransform: 'none' }}
            >
              Órdenes de pago
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2', flex: 1 }}>
              Orden de Pago{nroDoc ? ` ${nroDoc}` : ''}
              {anulada && (
                <Chip label="Anulada" size="small" color="default" variant="outlined" sx={{ ml: 1, verticalAlign: 'middle' }} />
              )}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => void handleImprimirPDF()}
                sx={{ textTransform: 'none', borderColor: '#1976d2', color: '#1976d2' }}
              >
                Imprimir
              </Button>
              <VerAsientoContableButton nroAsiento={orden.nroAsiento} sx={{ borderColor: '#1976d2', color: '#1976d2' }} />
              {!anulada && orden.puedeAnular && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={anulando ? <CircularProgress size={18} /> : <BlockIcon />}
                  onClick={() => void handleAnular()}
                  disabled={anulando}
                  sx={{ textTransform: 'none' }}
                >
                  Anular
                </Button>
              )}
            </Stack>
          </Stack>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Encabezado
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Fecha emisión</Typography>
              <Typography variant="body1">{formatearFecha(orden.fechaEmision)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Comprobante</Typography>
              <Typography variant="body1">{nroDoc || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Proveedor</Typography>
              <Typography variant="body1">{formatProveedorConNro(orden.nroProveedor, orden.nombre)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">CUIT</Typography>
              <Typography variant="body1">{orden.cuit || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Usuario</Typography>
              <Typography variant="body1">{orden.usuario || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Importe total</Typography>
              <Typography variant="body1">{formatCurrency(orden.importeTotal ?? 0)}</Typography>
            </Grid>
            {orden.formaDePago ? (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Forma de pago</Typography>
                <Typography variant="body1">{orden.formaDePago}</Typography>
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <ObservacionesConNotaProveedor nroProveedor={orden.nroProveedor}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Observaciones</Typography>
                  <Typography variant="body1">{orden.descripcion || '-'}</Typography>
                </Box>
              </ObservacionesConNotaProveedor>
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Medios de pago
          </Typography>
          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Medio</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Detalle</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Importe</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orden.mediosDePago.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                      No hay medios de pago para mostrar.
                    </TableCell>
                  </TableRow>
                ) : (
                  orden.mediosDePago.map((m, idx) => {
                    const extra = [m.nroCheque && `Cheque ${m.nroCheque}`, m.librador, m.nombreBanco]
                      .filter(Boolean)
                      .join(' · ');
                    return (
                      <TableRow key={`${m.mcp ?? 'medio'}-${idx}`}>
                        <TableCell>{m.tipoPago || '-'}</TableCell>
                        <TableCell>{m.mcp || '-'}</TableCell>
                        <TableCell>{[m.detalle, extra].filter(Boolean).join(' · ') || '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(m.importe ?? 0)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Documentos aplicados
          </Typography>
          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Importe</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Aplicado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aplicaciones.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                      No hay documentos imputados.
                    </TableCell>
                  </TableRow>
                ) : (
                  aplicaciones.map((a, idx) => {
                    const comprobante = formatNroDocumentoVenta(a.letra, a.nroSuc ?? 0, a.nroComprobante);
                    const href = pathDocumentoAplicado(a.nroTipoFormulario, a.nroIdformulario);
                    const etiqueta = comprobante || (a.nroIdformulario > 0 ? String(a.nroIdformulario) : '-');
                    return (
                      <TableRow key={`${a.nroIdformulario}-${a.nroMovCaja}-${idx}`}>
                        <TableCell>{tipoToPrefijo(a.nroTipoFormulario) || '-'}</TableCell>
                        <TableCell>
                          {href ? (
                            <Link component={RouterLink} to={href} underline="hover" sx={{ color: '#1976d2', fontWeight: 500 }}>
                              {etiqueta}
                            </Link>
                          ) : (
                            etiqueta
                          )}
                        </TableCell>
                        <TableCell>{formatearFecha(a.fechaEmision || a.fecha)}</TableCell>
                        <TableCell align="right">{formatCurrency(a.importeTotal ?? 0)}</TableCell>
                        <TableCell align="right">{formatCurrency(a.montoCancelado ?? 0)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                {orden.mediosDePago.length > 0 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary">Total medios</Typography>
                    <Typography>{formatCurrency(totalMedios)}</Typography>
                  </Stack>
                )}
                {aplicaciones.length > 0 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary">Total aplicado</Typography>
                    <Typography>{formatCurrency(totalAplicado)}</Typography>
                  </Stack>
                )}
                <Stack direction="row" justifyContent="space-between" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                  <Typography>Importe total</Typography>
                  <Typography>{formatCurrency(orden.importeTotal ?? 0)}</Typography>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
          <NumeroInternoMeta nro={orden.nroFormulario} />
        </CardContent>
      </Card>

      {pdfUrl && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={handleClosePDF}
          title={`Orden de Pago${nroDoc ? ` ${nroDoc}` : ''}`}
          documentId={`${orden.nroFormulario}`}
          documentType="ordenPago"
        />
      )}
    </Box>
  );
};

export default OrdenPagoDetalle;
